<template>
    <section class="mobile-detail-page">
        <header class="mobile-detail-header">
            <button type="button" aria-label="返回" @click="goBack">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="m15 5-7 7 7 7" />
                </svg>
            </button>
            <strong>问题详情</strong>
            <span></span>
        </header>

        <main v-if="loading" class="mobile-detail-state">正在加载问题…</main>
        <main v-else-if="error" class="mobile-detail-state">
            <p>问题暂时无法加载</p>
            <button type="button" @click="loadDetail">重新加载</button>
        </main>
        <main v-else class="mobile-detail-content">
            <article class="mobile-detail-question">
                <div class="mobile-detail-question__status">
                    <span>{{ typeTitle(answer.type_id) }}</span>
                    <em :class="{'mobile-detail-question__pending': answer.status !== 100}">
                        {{ answer.status === 100 ? '已解决' : '待解答' }}
                    </em>
                </div>
                <h1>{{ answer.title }}</h1>
                <div class="mobile-detail-question__user">
                    <img :src="avatar" :alt="author" @error="useDefaultAvatar">
                    <div>
                        <strong>{{ author }}</strong>
                        <span>提问者 · {{ createdDate }}</span>
                    </div>
                </div>
                <div class="mobile-detail-question__body" v-html="answer.content || ''"></div>
            </article>

            <section class="mobile-detail-replies">
                <div class="mobile-detail-replies__title">
                    <h2>{{ reply.total || 0 }} 个回答</h2>
                </div>
                <div v-if="reply.data.length === 0" class="mobile-detail-replies__empty">
                    还没有人回复这个问题
                </div>
                <article v-for="item in reply.data" :key="item.reply_id" class="mobile-detail-reply">
                    <div class="mobile-detail-reply__user">
                        <img :src="replyAvatar(item)" :alt="replyAuthor(item)" @error="useDefaultAvatar">
                        <div>
                            <strong>{{ replyAuthor(item) }}</strong>
                            <span>{{ replyIdentity(item) }} · {{ replyDateLabel(item) }}</span>
                        </div>
                        <em v-if="item.reply_id === answer.best_reply_id">最佳回答</em>
                    </div>
                    <div class="mobile-detail-reply__body" v-html="item.content || ''"></div>
                </article>
            </section>
        </main>
    </section>
</template>

<script>
import {formatTime} from '@/utils/tools';

export default {
    name: 'MobileQuestionDetail',
    data() {
        return {
            answer: {},
            reply: {
                total: 0,
                data: [],
            },
            loading: true,
            error: false,
        };
    },
    computed: {
        answerId() {
            return (this.$route.params.id || '').toString().split('.').shift();
        },
        avatar() {
            return this.answer.username && this.answer.username.face || '/static/bbs/images/answer/face.png';
        },
        author() {
            const user = this.answer.username || {};
            return user.nickname || user.username || 'BIGEMAP 用户';
        },
        createdDate() {
            return formatTime(this.answer.create_time, 'Y-m-d H:i');
        },
    },
    watch: {
        answerId() {
            this.loadDetail();
        },
    },
    created() {
        this.loadDetail();
    },
    methods: {
        loadDetail() {
            this.loading = true;
            this.error = false;
            this.reply = {total: 0, data: []};
            this.$bbs.get('/bbs/answer/view', {
                params: {answer_id: this.answerId},
                need: {loading: false},
            }).then((response) => {
                this.answer = response.data || {};
                if (!this.answer.answer_id) {
                    throw new Error('question not found');
                }
                return this.$bbs.get('/bbs/reply/index', {
                    params: {answer_id: this.answerId},
                    need: {loading: false},
                });
            }).then((response) => {
                const payload = response.data || {};
                this.reply.total = payload.total || 0;
                this.reply.data = Array.isArray(payload.data) ? payload.data : [];
            }).catch(() => {
                this.error = true;
            }).finally(() => {
                this.loading = false;
            });
        },
        goBack() {
            if (window.history.length > 1) {
                this.$router.back();
                return;
            }
            this.$router.push({path: '/index'});
        },
        typeTitle(typeId) {
            return ({100: 'BIGEMAP PRO', 200: 'BIGEMAP SERVER', 300: 'BIGEMAP APP'})[typeId] || '产品问答';
        },
        formatDate(time) {
            return formatTime(time, 'Y-m-d H:i');
        },
        replyUser(item) {
            return item.from_username || item.username || {};
        },
        replyAvatar(item) {
            return this.replyUser(item).face || '/static/bbs/images/answer/face.png';
        },
        replyAuthor(item) {
            const user = this.replyUser(item);
            return user.nickname || user.username || 'BIGEMAP 用户';
        },
        replyIdentity(item) {
            const answerUser = this.answer.username || {};
            const replyUser = this.replyUser(item);
            return answerUser.username && replyUser.username === answerUser.username ? '提问者' : '回答者';
        },
        replyDateLabel(item) {
            if (item.update_time && item.update_time > item.create_time) {
                return `编辑于 ${this.formatDate(item.update_time)}`;
            }
            return `回复于 ${this.formatDate(item.create_time)}`;
        },
        useDefaultAvatar(event) {
            const fallback = '/static/bbs/images/answer/face.png';
            if (!event.target.src.endsWith(fallback)) {
                event.target.src = fallback;
            }
        },
    },
};
</script>

<style scoped lang="less">
.mobile-detail-page {
    min-height: 100vh;
    color: #202a3b;
    background: #f4f6f9;
}

.mobile-detail-header {
    position: sticky;
    z-index: 10;
    top: 0;
    display: flex;
    height: 54px;
    padding: 0 12px;
    align-items: center;
    justify-content: space-between;
    background: #fff;
    border-bottom: 1px solid #e8ecf2;

    button,
    span {
        width: 38px;
    }

    button {
        display: flex;
        height: 40px;
        padding: 0;
        align-items: center;
        color: #435067;
        background: transparent;
        border: 0;

        svg {
            width: 23px;
            height: 23px;
            fill: none;
            stroke: currentColor;
            stroke-width: 1.9;
            stroke-linecap: round;
            stroke-linejoin: round;
        }
    }

    strong {
        font-size: 16px;
        font-weight: 600;
    }
}

.mobile-detail-state {
    display: flex;
    min-height: 420px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #8d98aa;
    font-size: 14px;

    button {
        margin-top: 12px;
        padding: 7px 15px;
        color: #2478f2;
        background: #eaf2ff;
        border: 0;
        border-radius: 5px;
    }
}

.mobile-detail-content {
    padding: 12px;
}

.mobile-detail-question,
.mobile-detail-replies {
    padding: 17px 15px;
    background: #fff;
    border: 1px solid #e8ecf2;
    border-radius: 8px;
}

.mobile-detail-question__status {
    display: flex;
    align-items: center;
    justify-content: space-between;

    span,
    em {
        padding: 3px 8px;
        font-size: 10px;
        font-style: normal;
        border-radius: 3px;
    }

    span {
        color: #2478f2;
        background: #edf4ff;
    }

    em {
        color: #239765;
        background: #eaf8f1;
    }

    .mobile-detail-question__pending {
        color: #d78825;
        background: #fff5e8;
    }
}

.mobile-detail-question h1 {
    margin: 15px 0;
    font-size: 21px;
    font-weight: 600;
    line-height: 31px;
}

.mobile-detail-question__user,
.mobile-detail-reply__user {
    display: flex;
    align-items: center;

    img {
        width: 34px;
        height: 34px;
        object-fit: cover;
        border-radius: 50%;
    }

    > div {
        display: flex;
        min-width: 0;
        margin-left: 9px;
        flex: 1;
        flex-direction: column;

        strong {
            color: #4b576b;
            font-size: 12px;
            font-weight: 500;
        }

        span {
            margin-top: 3px;
            color: #a0a9b8;
            font-size: 10px;
        }
    }
}

.mobile-detail-question__body,
.mobile-detail-reply__body {
    margin-top: 18px;
    overflow: hidden;
    color: #3f4b5f;
    font-size: 15px;
    line-height: 26px;
    overflow-wrap: anywhere;

    ::v-deep img {
        max-width: 100%;
        height: auto;
    }

    ::v-deep pre {
        max-width: 100%;
        padding: 12px;
        overflow-x: auto;
        background: #f4f6f9;
        border-radius: 5px;
    }
}

.mobile-detail-replies {
    margin-top: 12px;
}

.mobile-detail-replies__title {
    padding-bottom: 12px;
    border-bottom: 1px solid #edf0f4;

    h2 {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
    }
}

.mobile-detail-replies__empty {
    padding: 50px 0;
    color: #98a2b3;
    font-size: 13px;
    text-align: center;
}

.mobile-detail-reply {
    padding: 16px 0;
    border-bottom: 1px solid #edf0f4;

    &:last-child {
        border-bottom: 0;
    }
}

.mobile-detail-reply__user em {
    padding: 3px 7px;
    color: #239765;
    font-size: 10px;
    font-style: normal;
    background: #eaf8f1;
    border-radius: 3px;
}
</style>
